import { faCircleCheck, faCircleExclamation, faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type FC, type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import cn from "classnames";
import { Transition } from "semantic-ui-react";
import { CloseButton } from "components/buttons/closeButton";
import { AlertTypes } from "enums";
import styles from "./alert.module.scss";

export type AlertProps = {
  id: number | string;
  type: AlertTypes;
  textOnly?: boolean;
  action?: () => void;
  actionLabel?: string;
  title?: string;
  message: ReactNode;
  dismissInSeconds?: number;
  visible?: boolean;
  onDismissCompleted?: (id: number | string) => void;
  animateOnMount?: boolean;
  animationDuration?: number;
  createTime?: number;
  isPersistent?: boolean;
};

const typeClassNames = {
  [AlertTypes.success]: styles.success,
  [AlertTypes.error]: styles.error,
  [AlertTypes.warning]: styles.warning,
};

const Alert: FC<AlertProps> = (props) => {
  const {
    id,
    type,
    textOnly = false,
    action,
    actionLabel,
    title,
    message,
    visible = true,
    dismissInSeconds,
    isPersistent = false,
    onDismissCompleted,
    animateOnMount = true,
    animationDuration = 1000,
    createTime,
  } = props;

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [internalVisible, setInternalVisible] = useState(true);

  const handleHide = () => {
    onDismissCompleted && onDismissCompleted(id);
  };

  const handleClose = useCallback(() => {
    if (dismissInSeconds && dismissInSeconds > 0) {
      setInternalVisible(false);
    } else {
      onDismissCompleted && onDismissCompleted(id);
    }
  }, [id, onDismissCompleted, dismissInSeconds]);

  useEffect(() => {
    if (dismissInSeconds && dismissInSeconds > 0 && !isPersistent) {
      const delay = createTime
        ? Math.max(0, dismissInSeconds * 1000 - (Date.now() - createTime))
        : dismissInSeconds * 1000;

      timeoutRef.current = setTimeout(() => {
        handleClose();
      }, delay);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [dismissInSeconds, isPersistent, createTime, id, handleClose]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  let typeConfig;

  switch (type) {
    case AlertTypes.success:
      typeConfig = {
        name: "success",
        icon: faCircleCheck,
        title: title ?? "Success!",
      };
      break;
    case AlertTypes.error:
      typeConfig = {
        name: "error",
        icon: faTriangleExclamation,
        title: title ?? "Error!",
      };
      break;
    case AlertTypes.warning:
      typeConfig = {
        name: "warning",
        icon: faCircleExclamation,
        title,
      };
      break;
    default:
      return null;
  }

  const finalVisible = visible && internalVisible;

  return (
    <Transition
      transitionOnMount={animateOnMount}
      visible={finalVisible}
      unmountOnHide
      onHide={handleHide}
      duration={animationDuration}
    >
      <div className="transition-wrapper">
        <div
          className={cn(styles.alert, typeClassNames[type], { "text-only": textOnly })}
          data-testid="alert"
          data-alert-type={typeConfig.name}
        >
          <div className={styles.content}>
            <FontAwesomeIcon icon={typeConfig.icon} className={cn(styles.icon, typeClassNames[type])} />
            <div className={styles.message} data-testid="alert-message">
              <span className={styles.title}>{typeConfig.title}</span> {message}
            </div>
          </div>
          {action && actionLabel && (
            <button className={cn(styles.action, typeClassNames[type])} onClick={action}>
              {actionLabel}
            </button>
          )}
          {!dismissInSeconds && !isPersistent && !action && (
            <CloseButton className={styles.closeButton} inverted onClick={handleClose} />
          )}
        </div>
      </div>
    </Transition>
  );
};

export default Alert;
